import React from 'react';
import { TherapeuticArea } from '../../services/api/domains/therapeutic-areas/types';
import { modalStyles as styles } from './styles';
import useEditableModal, { ModalMode } from './hooks/useEditableModal';
import ModalFieldRenderer from './ModalFieldRenderer';
import {
  therapeuticAreaFields,
  therapeuticAreaAlwaysDisabledFields,
} from './configs/therapeuticAreaFields';
import LgmButton from '../LgmButton';
import { Edit, Save, X } from 'lucide-react';

interface TherapeuticAreaModalProps {
  therapeuticArea: TherapeuticArea;
  initialMode?: 'EDIT' | 'VIEW';
  save: (editedTherapeuticArea: TherapeuticArea) => void;
  close: () => void;
}

const TherapeuticAreaModal: React.FC<TherapeuticAreaModalProps> = ({
  therapeuticArea,
  initialMode = 'VIEW',
  save,
  close,
}) => {
  // Use the hook for all modal logic
  const {
    editedEntity: editedTherapeuticArea,
    setEditedEntity: setEditedTherapeuticArea,
    modalMode,
    hasUnsavedChanges,
    focusedField,
    setFocusedField,
    handleSave,
    handleClose,
    isFieldDisabled,
    getFloatingLabelStyle,
    getFloatingInputStyle,
    getPrimaryActionText,
    isSaving,
  } = useEditableModal({
    entity: therapeuticArea,
    initialMode,
    onSave: save,
    onClose: close,
    alwaysDisabledFields: therapeuticAreaAlwaysDisabledFields,
    styles,
  });

  return (
    <div style={styles.modalOverlayStyle}>
      <div style={styles.modalContentStyle}>
        <div style={styles.modalHeaderStyle}>
          <h2 style={{ ...styles.modalTitleStyle, marginBottom: 0 }}>
            {modalMode === 'EDIT'
              ? 'Edit Therapeutic Area'
              : 'Therapeutic Area Details'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LgmButton
              variant="flat"
              color={modalMode === 'VIEW' ? 'warning' : 'save'}
              onClick={handleSave}
              disabled={
                isSaving || (modalMode === 'EDIT' && !hasUnsavedChanges)
              }
              style={styles.modalHeaderIconTextButtonStyle}
              aria-label={getPrimaryActionText()}
              title={getPrimaryActionText()}>
              {modalMode === 'VIEW' ? <Edit size={16} /> : <Save size={16} />}
              {getPrimaryActionText()}
            </LgmButton>
            <LgmButton
              variant="flat"
              onClick={handleClose}
              disabled={isSaving}
              style={styles.modalCloseXStyle}
              hoverStyle={styles.modalCloseXHoverStyle}
              pressedStyle={styles.modalCloseXPressedStyle}
              aria-label="Close">
              <X size={16} />
            </LgmButton>
          </div>
        </div>

        <div style={styles.modalBodyStyleNoFooter}>
          {/* All therapeutic area fields */}
          <ModalFieldRenderer
            fields={therapeuticAreaFields}
            entity={editedTherapeuticArea}
            setEntity={setEditedTherapeuticArea}
            mode={modalMode}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            isFieldDisabled={isFieldDisabled}
            getFloatingLabelStyle={getFloatingLabelStyle}
            getFloatingInputStyle={getFloatingInputStyle}
            styles={styles}
            missingMessage="Unknown"
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(TherapeuticAreaModal);
export type { ModalMode };
